import { getCategoriesByProducts } from "@/js/repository/CategoryRepository";
import { getCategoryPath, getProductPath } from "@/js/utils/stringUtils";
import { getProducts, getProductsCount, getPromoCodeProducts, getPromoCodeProductsCount, ProductResponse } from "@/js/repository/ProductRepository";
import { ProductList } from "./products";
import Head from "next/head";
import { SiteInfo } from "@/globals";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import axios from "axios";
import { useRouter } from "next/router";
import { AdSenseBanner } from "@/components/app/AdSenseBanner";
import { useIsMobile } from "@/lib/useIsMobile";
import { convertCurrencySign } from "@/js/format/Format";
import { buildKeywordContent } from "@/lib/seo";

export async function getServerSideProps(context: any) {
  const { locale } = context;
  const { page } = context.query;
  const siteUrl = `https://${context.req.headers.host}`;
  
  const pageNumber = parseInt(page) || 1;
  const pageSize = 40 * pageNumber; // 현재 페이지에 따라 필요한 만큼의 데이터를 가져옴

  const categories = await getCategoriesByProducts(locale);
  const products = await getProducts(locale, 1, pageSize); // 1페이지부터 현재 페이지까지 데이터를 불러옴
  const productsCount = await getProductsCount(locale);
  const promoProducts = await getPromoCodeProducts(locale, 1, 5);
  const promoProductsCount = await getPromoCodeProductsCount(locale);
  
  const hasNext = productsCount > pageNumber * 40;

  return {
    props: { categories, products, productsCount, hasNext, pageNumber, siteUrl, promoProducts, promoProductsCount },
  }
}

export default function Home(props: any) {
  const { t } = useTranslation();
  const router = useRouter();
  const locale = router.locale;
  const isMobile = useIsMobile();

  const [productList, setProductList] = useState<ProductResponse[]>(props.products);
  const [hasNext, setHasNext] = useState<boolean>(props.hasNext);
  const [page, setPage] = useState<number>(props.pageNumber || 1);
  const [loading, setLoading] = useState(false);
  const homeUrl = props.pageNumber > 1 ? `${props.siteUrl}/?page=${props.pageNumber}` : props.siteUrl;
  const visibleCount = Math.min(page * 40, props.productsCount);
  const featuredProducts = productList.slice(0, 3);

  async function loadMore() {
    const nextPage = page + 1;
    setLoading(true);
    try {
      const response = await axios.post(`/api/index/products`, { page: nextPage, locale });
      if (response.status === 200) {
        const newProducts = response.data.products;
        setProductList([...productList, ...newProducts]);
        setPage(nextPage);
        setHasNext(response.data.hasNext);
        router.push(`/?page=${nextPage}`, undefined, { shallow: true });
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Head>
        <title>{`${SiteInfo.siteName} | 알리 핫딜 큐레이션 · 할인 상품 모음`}</title>
        <meta name="description" content={`${t("siteDescription")}`} />
        <meta
          name="keywords"
          content={buildKeywordContent([
            ...SiteInfo.primaryKeywords,
            SiteInfo.siteName,
            SiteInfo.koreanName,
            SiteInfo.tagline,
            '핫딜',
            '할인',
            '프로모션',
            '쿠폰',
          ])}
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={homeUrl} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={homeUrl} />
        <meta property="og:title" content={`${SiteInfo.siteName} | ${t("brandTagline")}`} />
        <meta property="og:description" content={`${t("siteDescription")}`} />
        <meta property="og:image" content={`${props.siteUrl}/logo.png`} />
        <meta property="og:site_name" content={SiteInfo.siteName} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={`${props.siteUrl}/logo.png`} />
        <meta name="twitter:title" content={`${SiteInfo.siteName} | ${t("brandTagline")}`} />
        <meta name="twitter:description" content={`${t("siteDescription")}`} />

        <script type="application/ld+json" dangerouslySetInnerHTML={
          {
            __html: JSON.stringify(
            {
              "@context": "https://schema.org",
              "@type": "WebSite",
              "name": SiteInfo.siteName,
              "alternateName": SiteInfo.koreanName,
              "slogan": SiteInfo.tagline,
              "description": t("siteDescription"),
              "url": props.siteUrl,
              "inLanguage": "ko-KR",
              "potentialAction": {
                "@type": "SearchAction",
                "target": `${props.siteUrl}/search?q={search_term_string}`,
                "query-input": "required name=search_term_string"
              },
              "publisher": {
                "@type": "Organization",
                "name": SiteInfo.siteName,
                "url": props.siteUrl,
                "logo": {
                  "@type": "ImageObject",
                  "url": `${props.siteUrl}/logo.png`
                }
              }
            }, null, 2)
          }
        }>
        </script>
        <script type="application/ld+json" dangerouslySetInnerHTML={
          {
            __html: JSON.stringify(
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": SiteInfo.siteName,
              "alternateName": SiteInfo.koreanName,
              "url": props.siteUrl,
              "logo": {
                "@type": "ImageObject",
                "url": `${props.siteUrl}/logo.png`
              },
              "foundingDate": "2025-05-28",
              "description": t("siteDescription"),
              "knowsAbout": ["AliExpress", "핫딜", "프로모션", "가격 비교", "쇼핑 큐레이션"],
              "contactPoint": [
                {
                  "@type": "ContactPoint",
                  "email": SiteInfo.supportEmail,
                  "contactType": "Support",
                  "areaServed": "KR",
                }
              ]
            }, null, 2)
          }
        }>
        </script>
        {props.products?.length > 0 && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ItemList",
              "numberOfItems": props.productsCount,
              "itemListElement": props.products.slice(0, 20).map((p: ProductResponse, i: number) => ({
                "@type": "ListItem",
                "position": i + 1,
                "url": `${props.siteUrl}${getProductPath(p.detail.product_id, p.detail.product_title)}`,
                "name": p.detail.product_title?.substring(0, 100)
              }))
            }, null, 2)
          }} />
        )}
      </Head>

      <section className="mb-8 grid gap-5 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="rounded-lg border border-black/10 bg-white p-6 shadow-sm sm:p-8">
          <p className="text-xs font-black uppercase tracking-normal text-[#fd384f]">{t("homeEyebrow")}</p>
          <h1 className="mt-3 max-w-3xl text-3xl font-black leading-tight text-[#101820] sm:text-5xl">{t("homeHeroTitle")}</h1>
          <p className="mt-4 max-w-2xl text-sm leading-6 text-gray-600 sm:text-base">{t("homeHeroDescription")}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            <a href="#deal-feed" className="rounded-lg bg-[#101820] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#fd384f]">{t("compareDeals")}</a>
            <a href="/promotions" className="rounded-lg border border-black/10 bg-[#f7f4ef] px-4 py-3 text-sm font-bold text-[#101820] transition hover:border-[#fd384f]/40 hover:text-[#fd384f]">{t("viewAllPromotions")}</a>
          </div>
          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg bg-[#f7f4ef] p-4">
              <div className="text-2xl font-black text-[#101820]">{props.productsCount.toLocaleString(locale)}</div>
              <div className="mt-1 text-xs font-semibold text-gray-500">{t("curatedNow")}</div>
            </div>
            <div className="rounded-lg bg-[#eef7f4] p-4">
              <div className="text-2xl font-black text-[#0f766e]">{props.categories?.length || 0}</div>
              <div className="mt-1 text-xs font-semibold text-gray-500">{t("otherCategories")}</div>
            </div>
            <div className="rounded-lg bg-[#fff1f2] p-4">
              <div className="text-2xl font-black text-[#fd384f]">{(props.promoProductsCount || 0).toLocaleString(locale)}</div>
              <div className="mt-1 text-xs font-semibold text-gray-500">{t("promotions")}</div>
            </div>
          </div>
        </div>

        {featuredProducts.length > 0 && (
          <div className="rounded-lg border border-black/10 bg-[#101820] p-4 text-white shadow-sm sm:p-5">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-black uppercase tracking-normal text-[#ffb000]">{t("todaysPicks")}</p>
                <h2 className="mt-1 text-xl font-black">{t("curatedByProsense")}</h2>
              </div>
              <a href="#deal-feed" className="text-sm font-bold text-white/70 hover:text-white">{t("showMore")}</a>
            </div>
            <div className="grid gap-3">
              {featuredProducts.map((product: ProductResponse) => {
                const salePrice = parseFloat(product.detail.target_sale_price);
                const originalPrice = parseFloat(product.detail.target_original_price);
                const discountRate = originalPrice > 0 ? Math.max(0, ((originalPrice - salePrice) / originalPrice) * 100) : 0;

                return (
                  <a
                    key={product.detail.product_id}
                    href={getProductPath(product.detail.product_id, product.detail.product_title)}
                    className="grid grid-cols-[84px_1fr] gap-3 rounded-lg bg-white/10 p-2 transition hover:bg-white/15"
                  >
                    <img
                      className="h-[84px] w-[84px] rounded-md bg-white object-contain"
                      src={product.detail.product_main_image_url}
                      alt={product.detail.product_title}
                      width={168}
                      height={168}
                      loading="lazy"
                    />
                    <div className="min-w-0 py-1">
                      <p className="line-clamp-2 text-sm font-bold leading-5">{product.detail.product_title}</p>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="text-lg font-black text-[#ff7a59]">{convertCurrencySign(product.detail.target_sale_price_currency, salePrice.toLocaleString(locale))}</span>
                        {discountRate > 0 && <span className="rounded-md bg-[#fd384f] px-2 py-1 text-xs font-black text-white">-{discountRate.toFixed(0)}%</span>}
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {props.promoProducts.length > 0 && (
        <section className="mb-8">
          <div className="mb-3 flex items-end justify-between gap-4">
            <div>
              <p className="text-xs font-black uppercase tracking-normal text-[#fd384f]">{t("promotions")}</p>
              <h2 className="text-2xl font-black text-[#101820]">프로모션 코드 상품</h2>
            </div>
            <a 
              href="/promotions" 
              className="rounded-lg border border-black/10 bg-white px-3 py-2 text-sm font-bold text-[#101820] hover:border-[#fd384f]/40 hover:text-[#fd384f]"
            >
              {t("viewAllPromotions")}
            </a>
          </div>
          <ProductList products={props.promoProducts} />
        </section>
      )}

      <section className="mb-8 grid gap-4 lg:grid-cols-3" aria-label={t("curationStandard")}>
        <div className="lg:col-span-3">
          <p className="text-xs font-black uppercase tracking-normal text-[#fd384f]">{t("curationStandard")}</p>
          <h2 className="mt-1 text-2xl font-black text-[#101820]">{t("curationStandardDescription")}</h2>
        </div>
        {[t("curationDiscount"), t("curationDemand"), t("curationFreshness")].map((message) => (
          <div key={message} className="rounded-lg border border-black/10 bg-white p-4 text-sm font-semibold leading-6 text-gray-700 shadow-sm">
            {message}
          </div>
        ))}
      </section>

      <section id="deal-feed" className="scroll-mt-24">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-normal text-[#fd384f]">{t("dealFeed")}</p>
            <h2 className="mt-1 text-2xl font-black text-[#101820]">{t("hotDeals")}</h2>
            {props.productsCount > 0 && (
              <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-600">
                {t("dealFeedDescription")}
              </p>
            )}
            {props.productsCount > 40 && (
              <p className="mt-1 text-sm text-gray-500">{t("showingCount", { from: 1, to: visibleCount, total: props.productsCount })}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {props.categories?.slice(0, 6).map((cat: { id: number; name: string }) => (
              <a key={cat.id} href={getCategoryPath(cat.id, cat.name)} className="rounded-lg border border-black/10 bg-white px-3 py-2 text-sm font-semibold hover:border-[#fd384f]/40 hover:text-[#fd384f]">{cat.name}</a>
            ))}
          </div>
        </div>

        <div className="my-6">
          <AdSenseBanner id="home-content-top" slot="4220411723" height={isMobile ? 100 : 120} />
        </div>

        <ProductList products={productList} />

        <div className="my-6">
          <AdSenseBanner id="home-content-bottom" slot="4220411723" height={isMobile ? 180 : 240} />
        </div>
      </section>

      { hasNext && (
        <div className="text-center mt-12">
          <button
            className="rounded-lg bg-[#101820] px-14 py-3 text-md font-bold text-white transition hover:bg-[#fd384f] disabled:opacity-70"
            onClick={() => loadMore()}
            disabled={loading}
            title={t("showMore")}
          >
            {loading ? t("loadingMore") : t("showMore")}
          </button>
        </div>
      )}
    </>
  );
}
